export type AdminModuleDifficulty = 'Básico' | 'Intermediário' | 'Avançado';
export type AdminModuleView = 'simple' | 'advanced';

export type AdminQuickFilter = {
  id: string;
  label: string;
  field: string;
  operator?: 'equals' | 'not_equals' | 'truthy' | 'falsy';
  value?: string | number | boolean;
};

export type AdminModule = {
  id: string;
  label: string;
  description: string;
  endpoint: string;
  category: string;
  examples?: string[];
  tags?: string[];
  difficulty?: AdminModuleDifficulty;
  defaultView?: AdminModuleView;
  essentialColumns?: string[];
  essentialFields?: string[];
  fieldHelp?: Record<string, string>;
  fieldLabels?: Record<string, string>;
  fieldOrder?: string[];
  quickFilters?: AdminQuickFilter[];
};

export const ADMIN_MODULES: AdminModule[] = [
  {
    id: 'clientes',
    label: 'Clientes (municípios)',
    description: 'Cadastre a prefeitura/secretaria e habilite o ambiente de trabalho.',
    endpoint: '/admin/clientes',
    category: 'Core',
    examples: ['Ex: Prefeitura de Arapiraca', 'Ex: SEMED Campinas'],
    tags: ['comece-aqui', 'sistema'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome', 'slug', 'created_at'],
    essentialFields: ['nome', 'slug'],
    fieldLabels: { nome: 'Nome do cliente', slug: 'Identificador (slug)' },
    fieldHelp: {
      nome: 'Nome oficial do município ou secretaria.',
      slug: 'Usado em URLs e integrações. Sem espaços.',
    },
    fieldOrder: ['nome', 'slug'],
  },
  {
    id: 'clientes-config',
    label: 'Configurações do cliente',
    description: 'Ajustes finos do cliente selecionado.',
    endpoint: '/admin/clientes-config',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
  },
  {
    id: 'clientes-feature-flags',
    label: 'Feature flags',
    description: 'Ative/desative módulos de forma segura.',
    endpoint: '/admin/clientes-feature-flags',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'clientes-temas',
    label: 'Identidade visual',
    description: 'Defina cores, logo e rodapé do cliente.',
    endpoint: '/admin/clientes-temas',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'cliente', 'cor_primaria', 'cor_secundaria'],
    essentialFields: ['cliente', 'cor_primaria', 'cor_secundaria', 'logo_url'],
  },
  {
    id: 'eixos',
    label: 'Eixos AVA',
    description: 'Cadastre eixos para controlar acesso aos cursos do AVA.',
    endpoint: '/admin/eixos',
    category: 'Core',
    tags: ['sistema', 'ava'],
    difficulty: 'BÃ¡sico',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome', 'ativo', 'ordem_exibicao'],
    essentialFields: ['nome', 'descricao', 'ativo', 'ordem_exibicao'],
    fieldLabels: {
      nome: 'Nome do eixo',
      descricao: 'Descricao',
      ativo: 'Ativo',
      ordem_exibicao: 'Ordem de exibicao',
    },
    fieldHelp: {
      nome: 'Nome visivel para administradores ao vincular usuarios e cursos.',
      ativo: 'Eixos inativos deixam de liberar acesso aos cursos vinculados.',
    },
    fieldOrder: ['nome', 'descricao', 'ativo', 'ordem_exibicao'],
    quickFilters: [
      { id: 'ativos', label: 'Ativos', field: 'ativo', operator: 'truthy' },
      { id: 'inativos', label: 'Inativos', field: 'ativo', operator: 'falsy' },
    ],
  },
  {
    id: 'usuarios',
    label: 'Usuários e acessos',
    description: 'Crie contas, papéis e permissões.',
    endpoint: '/admin/usuarios',
    category: 'Core',
    examples: ['Ex: Coordenador da SEMED', 'Ex: Redator GT'],
    tags: ['comece-aqui', 'sistema'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome', 'email', 'role', 'cliente', 'escola', 'eixos', 'is_active'],
    essentialFields: ['nome', 'email', 'role', 'cliente', 'escola', 'eixos', 'password'],
    fieldLabels: {
      nome: 'Nome completo',
      email: 'E-mail',
      role: 'Perfil',
      is_active: 'Ativo',
      password: 'Senha inicial',
      cliente: 'Cliente',
      escola: 'Escola',
      eixos: 'Eixos AVA',
    },
    fieldHelp: {
      eixos: 'Restrinja quais cursos do AVA o usuario pode acessar quando cursos estiverem vinculados a eixos.',
      role: 'Defina o perfil principal (Admin, Diretor, Coordenador, Professor, Redator, Revisor ou Membro GT).',
      escola: 'Obrigatório para Diretor, Coordenador Pedagógico e Professor.',
      password: 'Defina uma senha temporária. O usuário pode alterar depois.',
    },
    quickFilters: [
      { id: 'ativos', label: 'Ativos', field: 'is_active', operator: 'truthy' },
      { id: 'bloqueados', label: 'Bloqueados', field: 'is_active', operator: 'falsy' },
    ],
    fieldOrder: ['nome', 'email', 'role', 'cliente', 'escola', 'eixos', 'is_active', 'password'],
  },
  {
    id: 'audit-logs',
    label: 'Auditoria de mudanças',
    description: 'Histórico técnico do que mudou e quem mudou.',
    endpoint: '/admin/audit-logs',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'throttle-blocks',
    label: 'Bloqueios e limites',
    description: 'Controle de abusos e tentativas excessivas.',
    endpoint: '/admin/throttle-blocks',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'score-entries',
    label: 'Pontuação (gamificação)',
    description: 'Registros de pontos das trilhas.',
    endpoint: '/admin/score-entries',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
  },
  {
    id: 'session-logs',
    label: 'Sessões de usuário',
    description: 'Logins, acessos e rastros.',
    endpoint: '/admin/session-logs',
    category: 'Core',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'gts',
    label: 'Grupos de Trabalho (GTs)',
    description: 'Organize os GTs por etapa e membros.',
    endpoint: '/admin/gts',
    category: 'Currículo',
    examples: ['Ex: GT Alfabetização', 'Ex: GT Ciências 6º ao 9º'],
    tags: ['comece-aqui', 'curriculo'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome', 'etapa', 'membros'],
    essentialFields: ['nome', 'etapa', 'membros'],
    fieldHelp: {
      nome: 'Nome do GT conforme a organização da SEMED.',
      etapa: 'Etapa principal (ex.: Educação Infantil, Anos Finais).',
      membros: 'Selecione os usuários que participam do GT.',
    },
    fieldOrder: ['nome', 'etapa', 'membros'],
  },
  {
    id: 'escolas',
    label: 'Escolas',
    description: 'Cadastre as escolas do cliente para uso no curriculo.',
    endpoint: '/admin/escolas',
    category: 'CurrÇðculo',
    examples: ['Ex: Escola Municipal Centro', 'Ex: EMEF Aurora'],
    tags: ['comece-aqui', 'curriculo'],
    difficulty: 'BÇ­sico',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome'],
    essentialFields: ['nome'],
    fieldHelp: {
      nome: 'Nome oficial da escola dentro do cliente selecionado.',
    },
    fieldOrder: ['nome'],
  },
  {
    id: 'areas',
    label: 'Áreas e componentes',
    description: 'Defina áreas (Matemática, Linguagens etc.)',
    endpoint: '/admin/areas',
    category: 'Currículo',
    tags: ['curriculo'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome', 'gts'],
    essentialFields: ['nome', 'descricao_html', 'gts'],
  },
  {
    id: 'tarefas',
    label: 'Trilhas pedagógicas',
    description: 'Crie as trilhas (tarefas) e a ordem das etapas.',
    endpoint: '/admin/tarefas',
    category: 'Currículo',
    examples: ['Ex: Etapa I · Concepção Geral'],
    tags: ['comece-aqui', 'curriculo'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'nome', 'ordem', 'etapa', 'tipo', 'status'],
    essentialFields: ['nome', 'ordem', 'etapa', 'tipo', 'status'],
    fieldHelp: {
      nome: 'Nome visível para o GT.',
      ordem: 'Sequência de execução da trilha.',
      etapa: 'Etapa pedagógica associada.',
      tipo: 'Perguntas ou Oficina.',
    },
    fieldOrder: ['nome', 'ordem', 'etapa', 'tipo', 'status'],
    quickFilters: [
      { id: 'ativas', label: 'Ativas', field: 'status', operator: 'not_equals', value: 'concluida' },
      { id: 'concluidas', label: 'Concluídas', field: 'status', operator: 'equals', value: 'concluida' },
    ],
  },
  {
    id: 'perguntas',
    label: 'Perguntas dos blocos',
    description: 'Perguntas que guiam a escrita do GT.',
    endpoint: '/admin/perguntas',
    category: 'Currículo',
    examples: ['Ex: Qual a proposta pedagógica?'],
    tags: ['comece-aqui', 'curriculo'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'tarefa', 'ordem', 'texto', 'obrigatoria'],
    essentialFields: ['tarefa', 'ordem', 'texto', 'obrigatoria', 'gts'],
    fieldHelp: {
      tarefa: 'Trilha à qual a pergunta pertence.',
      texto: 'Texto completo que o GT deve responder.',
      gts: 'Opcional: restrinja a pergunta a GTs específicos.',
    },
    fieldOrder: ['tarefa', 'ordem', 'texto', 'obrigatoria', 'gts'],
  },
  {
    id: 'respostas',
    label: 'Respostas enviadas',
    description: 'Conteúdos escritos pelos GTs.',
    endpoint: '/admin/respostas',
    category: 'Currículo',
    tags: ['curriculo'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
    essentialColumns: ['id', 'gt', 'pergunta', 'updated_at'],
  },
  {
    id: 'anexos',
    label: 'Anexos de respostas',
    description: 'Arquivos anexados pelos GTs.',
    endpoint: '/admin/anexos',
    category: 'Currículo',
    tags: ['curriculo'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
  },
  {
    id: 'textos-unicos',
    label: 'Textos únicos',
    description: 'Texto consolidado por GT e etapa.',
    endpoint: '/admin/textos-unicos',
    category: 'Currículo',
    tags: ['curriculo'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'gt', 'tarefa', 'updated_at'],
    essentialFields: ['gt', 'tarefa', 'conteudo_html'],
  },
  {
    id: 'textos-colaborativos',
    label: 'Textos colaborativos',
    description: 'Rascunhos e versões em colaboração.',
    endpoint: '/admin/textos-colaborativos',
    category: 'Currículo',
    tags: ['curriculo'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'gt', 'titulo', 'updated_at'],
    essentialFields: ['gt', 'titulo', 'conteudo_html'],
  },
  {
    id: 'quadros',
    label: 'Quadros colaborativos',
    description: 'Quadros usados em oficinas e atividades.',
    endpoint: '/admin/quadros',
    category: 'Quadros',
    tags: ['curriculo'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'gt', 'template', 'updated_at'],
    essentialFields: ['gt', 'template', 'area'],
  },
  {
    id: 'quadros-linhas',
    label: 'Linhas do quadro',
    description: 'Configuração de linhas para cada quadro.',
    endpoint: '/admin/quadros-linhas',
    category: 'Quadros',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'quadros-colunas',
    label: 'Colunas do quadro',
    description: 'Configuração de colunas para cada quadro.',
    endpoint: '/admin/quadros-colunas',
    category: 'Quadros',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'quadros-celulas',
    label: 'Células do quadro',
    description: 'Conteúdos detalhados do quadro.',
    endpoint: '/admin/quadros-celulas',
    category: 'Quadros',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'formularios',
    label: 'Formulários dinâmicos',
    description: 'Estruturas de formulários personalizados.',
    endpoint: '/admin/formularios',
    category: 'Formulários',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'campos-dinamicos',
    label: 'Campos dinâmicos',
    description: 'Campos usados nos formulários.',
    endpoint: '/admin/campos-dinamicos',
    category: 'Formulários',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'respostas-campos',
    label: 'Respostas de formulários',
    description: 'Preenchimentos dos formulários.',
    endpoint: '/admin/respostas-campos',
    category: 'Formulários',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'revisoes',
    label: 'Revisões e pendências',
    description: 'Acompanhe o que está em revisão ou aprovado.',
    endpoint: '/admin/revisoes',
    category: 'Revisões',
    examples: ['Ex: Blocos devolvidos', 'Ex: Revisões pendentes'],
    tags: ['comece-aqui', 'revisao'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'alvo_tipo', 'alvo_id', 'status', 'revisor', 'solicitante', 'updated_at'],
    essentialFields: ['alvo_tipo', 'alvo_id', 'status', 'revisor', 'solicitante'],
    quickFilters: [
      { id: 'pendentes', label: 'Pendentes', field: 'status', operator: 'equals', value: 'em_revisao' },
      { id: 'devolvidas', label: 'Devolvidas', field: 'status', operator: 'equals', value: 'reprovado' },
      { id: 'validadas', label: 'Validadas', field: 'status', operator: 'equals', value: 'aprovado' },
    ],
  },
  {
    id: 'reviewer-assignments',
    label: 'Atribuições de revisor',
    description: 'Defina escopo de atuação dos revisores.',
    endpoint: '/admin/reviewer-assignments',
    category: 'Revisões',
    tags: ['revisao'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'reviewer', 'scope_type', 'scope_id', 'cliente'],
    essentialFields: ['reviewer', 'scope_type', 'scope_id'],
  },
  {
    id: 'review-decisions',
    label: 'Decisões e recomendações',
    description: 'Histórico de recomendações do revisor/redator.',
    endpoint: '/admin/review-decisions',
    category: 'Revisões',
    tags: ['revisao'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
  },
  {
    id: 'exports',
    label: 'Exportações',
    description: 'Gere documentos para impressão ou entrega.',
    endpoint: '/admin/exports',
    category: 'Revisões',
    tags: ['comece-aqui'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'status', 'created_at', 'updated_at'],
  },
  {
    id: 'notificacoes',
    label: 'Notificações (Mural)',
    description: 'Avisos e comunicados internos.',
    endpoint: '/admin/notificacoes',
    category: 'Comunicação',
    examples: ['Ex: Aviso de reunião', 'Ex: Comunicado de prazo'],
    tags: ['comece-aqui', 'comunicacao'],
    difficulty: 'Básico',
    defaultView: 'simple',
    essentialColumns: ['id', 'tipo', 'usuario', 'lida', 'created_at'],
    essentialFields: ['tipo', 'payload_json', 'usuario'],
  },
  {
    id: 'comentarios',
    label: 'Comentários',
    description: 'Discussões e ajustes por trecho.',
    endpoint: '/admin/comentarios',
    category: 'Comunicação',
    tags: ['comunicacao'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
  },
  {
    id: 'midias',
    label: 'Mídias da biblioteca',
    description: 'Links e recursos de apoio.',
    endpoint: '/admin/midias',
    category: 'Biblioteca',
    tags: ['biblioteca'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'titulo', 'url', 'gt', 'pergunta'],
    essentialFields: ['titulo', 'url', 'descricao', 'gt', 'pergunta'],
  },
  {
    id: 'blocos',
    label: 'Blocos de texto',
    description: 'Referências textuais usadas como modelo.',
    endpoint: '/admin/blocos',
    category: 'Biblioteca',
    tags: ['biblioteca'],
    difficulty: 'Intermediário',
    defaultView: 'simple',
    essentialColumns: ['id', 'titulo', 'tags'],
    essentialFields: ['titulo', 'conteudo_html', 'tags'],
  },
  {
    id: 'consultas-publicas',
    label: 'Consultas públicas',
    description: 'Configure consultas e documentos.',
    endpoint: '/admin/consultas-publicas',
    category: 'Consultas',
    tags: ['sistema'],
    difficulty: 'Intermediário',
    defaultView: 'advanced',
  },
  {
    id: 'manifestacoes-publicas',
    label: 'Manifestações públicas',
    description: 'Feedback público das consultas.',
    endpoint: '/admin/manifestacoes-publicas',
    category: 'Consultas',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'meb-threads',
    label: 'MEB Threads',
    description: 'Conversas do assistente MEB.',
    endpoint: '/admin/meb-threads',
    category: 'MEB',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
  {
    id: 'meb-mensagens',
    label: 'MEB Mensagens',
    description: 'Mensagens do assistente MEB.',
    endpoint: '/admin/meb-mensagens',
    category: 'MEB',
    tags: ['sistema'],
    difficulty: 'Avançado',
    defaultView: 'advanced',
  },
];
